/** Temas de ayuda · Inventario: productos, entregas, inventario, ajustes, roturas y detalles, traslados y flota. */
import type { TemaAyuda } from '../tipos.ts';

const APRUEBA_AJUSTES = { clave: 'inventario.ajustes', nivel: 'total' } as const;
const CONTEOS = { clave: 'inventario.conteos', nivel: 'total' } as const;
const FIJA_PRECIOS = { clave: 'productos.precios' } as const;
const REINTENTO = { mensaje: 'Esa clave de operación ya se usó con otros datos: vuelve a abrir el formulario y registra de nuevo', que_hacer: 'Se reintentó después de cambiar algo. Cierra la ventana, revisa si ya quedó registrado y, si no, regístralo de nuevo.' };

export const TEMAS_INVENTARIO: TemaAyuda[] = [
  // ─── Productos ──────────────────────────────────────────────────────────────
  {
    id: 'productos',
    titulo: 'Productos',
    rutas: ['/productos'],
    permiso: { clave: 'productos.consultar' },
    resumen: 'El catálogo de la empresa: busca un producto, mira cuánto hay disponible en tu sucursal y su precio detal. Un clic en la fila abre su ficha.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Busca por código, nombre o marca. Filtra con el desplegable **Categoría** y las casillas **Bajo mínimo** e **Incluir inactivos**.',
          '**Disponible**: la existencia de tu sucursal menos lo vendido que el cliente aún no retira. Debajo sale lo disponible en la tienda y en cada depósito.',
          'En rojo, el almacén que está bajo su mínimo; en ámbar, el que está cerca. El mínimo se compara con lo disponible, no con lo que hay en el estante.',
          'Un servicio dice «sin inventario». La marca **Frágil** es lo que se vende por caja y por pieza.',
          '**Precio detal (unidad)**: en dólares, con los bolívares debajo.',
          'Salen 25 por página: **Anterior** y **Siguiente** cambian de página.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Los botones de arriba',
        items: [
          '**Descuentos**: los descuentos programados. Todos ven la lista.',
          '**Combos y recetas**: los kits y las recetas por medida (como el techo en dry wall por m²) que se venden de una vez. Todos los ven; los crea la dueña.',
          '**Cargar Excel** y **Nuevo producto**: solo para quien fija precios (la dueña, o a quien ella se lo dé).',
        ],
      },
      { tipo: 'nota', texto: 'Sin sucursal elegida (consola global) no salen **Disponible** ni el filtro **Bajo mínimo**: elige la sucursal en la cabecera.' },
    ],
    mensajes: [{ mensaje: 'No se pudo cargar', que_hacer: 'Se cayó la conexión con el servidor; los productos no han desaparecido. Pulsa **Reintentar** cuando vuelva.' }],
    manual: '14.1',
    relacionados: ['productos-ficha', 'inventario', 'pos', 'combos'],
    claves: ['catálogo', 'buscar producto', 'precio', 'disponible', 'mínimo', 'inactivos', 'código'],
  },
  {
    id: 'productos.formulario',
    padre: 'productos',
    titulo: 'Crear o editar un producto',
    permiso: FIJA_PRECIOS,
    resumen: 'La ficha básica del producto: código, nombre, categoría, tipo, fracción de venta, unidad, despiece y medida de la lámina. Los precios se ponen después, en la ficha.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Crear un producto',
        pasos: [
          'En **Productos**, pulsa **Nuevo producto**.',
          'Llena **Código** (único) y **Nombre**. Si aplica: **Descripción**, **Categoría**, **Subcategoría**, **Color** y **Marca**.',
          'Elige el **Tipo**: **Producto (lleva inventario)** o **Servicio (corte, flete, instalación…)**.',
          'Elige en qué fracción **Se vende en** y la **Unidad**.',
          'Si se vende por caja y por pieza, marca **Frágil (despiece)** y elige el **Producto pieza suelta** y las **Piezas por caja**.',
          'Si es una lámina de vidrio, escribe el **Largo (mm)**, el **Ancho (mm)** y el **Espesor (mm)**.',
          'Pulsa **Crear producto**. Se abre su ficha para poner presentaciones y precios.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cada campo',
        items: [
          '**Código**: no se repite y se guarda en mayúsculas.',
          '**Subcategoría**: solo salen las de la categoría elegida. Las categorías las crea la dueña en **Configuración**.',
          '**Servicio**: se factura igual, pero no lleva inventario: no se aparta, no se entrega y no entra a conteos.',
          '**Se vende en**: Solo enteros, Medios (0,5), Cuartos (0,25), Décimas (0,1) o Centésimas (0,01) para peso o metros. Si no eliges, queda en enteros; kg, mts, m2 y lt quedan en centésimas.',
          '**Unidad**: uni, kg, mts, m2, caja, saco, juego, galon, lt, par, rollo o pza.',
          '**Producto pieza suelta**: escribe en el buscador de arriba para encontrarlo. Al despiezar, cada caja se convierte en ese número de piezas.',
          '**Lámina de vidrio**: con la medida se revisa que las piezas de una rotura quepan y se arma el nombre de cada pieza. Sin la medida, la lámina no sale para reportar una rotura.',
        ],
      },
      { tipo: 'nota', texto: 'Para cambiar la ficha después: en la ficha del producto, **Editar ficha** y **Guardar cambios**. El costo no se escribe aquí: lo mueven las compras.' },
    ],
    mensajes: [
      { mensaje: 'Ya existe un producto con el código …', que_hacer: 'Usa otro código, o busca el producto que ya existe.' },
      { mensaje: 'La medida de la lámina lleva el largo y el ancho en milímetros', que_hacer: 'Escribe los dos, o deja los dos en blanco si no es una lámina.' },
      { mensaje: 'El despiece requiere producto destino Y factor de piezas por caja', que_hacer: 'Elige el **Producto pieza suelta** y escribe las **Piezas por caja**.' },
      { mensaje: 'La subcategoría no pertenece a la categoría elegida', que_hacer: 'Elige primero la categoría y después su subcategoría.' },
      { mensaje: 'Este producto tiene existencia o material por entregar: no puede pasar a servicio', que_hacer: 'Un servicio no lleva inventario. Lleva primero su existencia a cero con un ajuste.' },
    ],
    manual: '14.2',
    relacionados: ['productos-ficha', 'roturas', 'inventario'],
    claves: ['nuevo producto', 'código', 'servicio', 'fracción', 'unidad', 'frágil', 'despiece', 'lámina', 'medida', 'editar ficha'],
  },
  {
    id: 'productos.descuentos',
    padre: 'productos',
    titulo: 'Descuentos programados',
    resumen: 'Un porcentaje de descuento para un producto, una categoría o una marca entre dos fechas. Se aplica solo en el punto de venta y en las cotizaciones, sin pedir clave.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Programar un descuento',
        permiso: { clave: 'productos.descuentos' },
        pasos: [
          'En **Productos**, pulsa **Descuentos**.',
          'Escribe el **Nombre** (por ejemplo «Semana del pego»).',
          'En **Se aplica a** elige **Un producto**, **Una categoría** o **Una marca**, y luego cuál. La marca va tal como está escrita en la ficha de los productos.',
          'Escribe el **Descuento %** y las fechas **Desde** y **Hasta**.',
          'En **Tienda** deja **Todas las tiendas** o elige una.',
          'Pulsa **Programar descuento**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cómo funciona',
        items: [
          'El renglón de la venta o de la cotización dice qué descuento se aplicó.',
          'Si un producto cae en varios descuentos, rige el mayor.',
          'Estados: **Programado** (aún no empieza), **Vigente**, **Vencido** o **Desactivado**.',
          '**Desactivar** lo apaga y **Activar** lo enciende. **Desactivar** pide confirmar en una ventana: deja de aplicarse desde ya y las ventas que ya lo llevan quedan igual. Uno vencido ya no se toca.',
          'Todos ven la lista; los crea quien tiene el permiso (la dueña).',
        ],
      },
      { tipo: 'nota', texto: 'Si no eres la dueña, programa el descuento solo para tu tienda: los de todas las tiendas o de otra tienda son de ella.', permiso: { clave: 'productos.descuentos' } },
    ],
    mensajes: [
      { mensaje: 'Ponle un nombre (mínimo 3 caracteres)', que_hacer: 'Escribe el **Nombre** del descuento.' },
      { mensaje: 'Indica a qué se aplica el descuento', que_hacer: 'Elige el producto o la categoría, o escribe la marca.' },
      { mensaje: 'La fecha final no puede ser anterior a la inicial', que_hacer: 'Corrige **Desde** y **Hasta**.' },
      { mensaje: 'La fecha final ya pasó: el descuento no se aplicaría nunca', que_hacer: 'Pon en **Hasta** una fecha de hoy en adelante.' },
      { mensaje: 'El porcentaje no puede superar 100', que_hacer: 'Corrige el **Descuento %**.' },
      { mensaje: 'Solo puedes programar descuentos de tu tienda; los de todas las tiendas o de otra son de la dueña', que_hacer: 'En **Tienda** elige la tuya.' },
    ],
    manual: '14.5',
    relacionados: ['pos', 'cotizaciones'],
    claves: ['descuento', 'promoción', 'oferta', 'porcentaje', 'vigente'],
  },
  {
    id: 'productos.excel',
    padre: 'productos',
    titulo: 'Cargar productos desde Excel',
    permiso: FIJA_PRECIOS,
    resumen: 'Carga de una vez muchos productos con sus precios. El sistema primero revisa el archivo y te muestra qué va a crear, qué deja igual y qué filas tienen error.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar',
        pasos: [
          'En **Productos**, pulsa **Cargar Excel**.',
          'Pulsa **Descargar plantilla**. Trae los títulos, dos filas de ejemplo y una hoja de **Instrucciones**.',
          'Borra las filas de ejemplo y escribe un producto por fila. No cambies los títulos de la primera fila.',
          'Pulsa **Elegir archivo** y sube el Excel. El sistema revisa y todavía no carga nada.',
          'Si quieres cambiar los que ya existen, marca **Si ya existe (mismo código), actualizarlo con lo del archivo**. La revisión se rehace.',
          'Pulsa **Cargar … productos**. Una carga grande puede tardar un minuto: no cierres la ventana.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Las columnas',
        items: [
          'Código, Nombre, Unidad, Categoría, Subcategoría, Marca, Color, Tipo, Frágil y un precio por cada lista de precios.',
          '**Código**, **Nombre** y **Unidad** son obligatorios. Si el código empieza por cero, escríbelo como texto.',
          '**Tipo**: «producto» o «servicio» (vacío es producto). **Frágil**: «si» o «no» (vacío es no).',
          'Precios en dólares, por unidad. Vacío es sin precio en esa lista.',
          'Una categoría que no existe se crea si carga la dueña. Si carga otra persona, la categoría tiene que existir.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa al cargar',
        items: [
          'Las filas con error no entran; las demás sí. En la revisión, los errores salen primero.',
          'Si alguna fila falla, la ventana queda abierta con **Carga hecha.** y el resultado de cada fila: **Creado**, **Actualizado**, **Quedó igual** o **No entró**, con el motivo.',
          'Corrige esas filas y sube el archivo otra vez: las que ya entraron se dejan igual.',
          'Con la casilla de actualizar marcada solo cambian el nombre y los precios. Un precio vacío no borra el que había. El precio va a la presentación activa de 1 unidad (la que nace con cada ficha), no a una de menos unidades ni a una inactiva.',
          'Solo archivos .xlsx, de hasta 2 MB y 1000 filas.',
        ],
      },
      { tipo: 'nota', texto: 'La existencia no va en este Excel: se carga en **Inventario**, con **Existencia inicial (Excel)**. El costo entra con la primera compra.' },
    ],
    mensajes: [
      { mensaje: 'El archivo no es un Excel (.xlsx)', que_hacer: 'Usa la plantilla del sistema.' },
      { mensaje: 'El archivo pesa más de 2 MB', que_hacer: 'Divídelo en varios archivos.' },
      { mensaje: 'Al Excel le faltan las columnas …', que_hacer: 'Se cambiaron o borraron títulos. Descarga la plantilla y llena esa.' },
      { mensaje: 'El Excel tiene … filas; el máximo por carga es 1000', que_hacer: 'Divídelo en varios archivos.' },
      { mensaje: 'La categoría «…» no existe: pídele a la dueña que la cree en Configuración', que_hacer: 'La dueña la crea en **Listas de precio y categorías**.' },
      { mensaje: 'La subcategoría «…» necesita su categoría en la columna «Categoría»', que_hacer: 'Escribe la categoría en esa fila.' },
    ],
    manual: '29.4',
    relacionados: ['inventario'],
    claves: ['Excel', 'carga masiva', 'plantilla', 'importar', 'xlsx'],
  },
  {
    id: 'productos-ficha',
    titulo: 'Ficha del producto',
    rutas: ['/productos/:id'],
    permiso: { clave: 'productos.consultar' },
    resumen: 'Todo de un producto: sus presentaciones y precios por lista, el stock de cada almacén, los mínimos, el histórico de precios y, para quien ve costos, su costo real.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiar precios',
        permiso: FIJA_PRECIOS,
        pasos: [
          'En **Presentaciones y precios ($ divisa por unidad)**, escribe el precio en dólares **por unidad** en la columna de cada lista.',
          'Pulsa **Guardar precios**. El botón dice cuántas casillas cambiaste.',
          'Los bolívares y el «$ a BCV» salen solos de las tasas del día.',
          'Cada cambio queda en el **Histórico de precios** con tu usuario.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Agregar una presentación',
        permiso: FIJA_PRECIOS,
        pasos: ['Pulsa **Presentación**.', 'Escribe el nombre (por ejemplo «Caja x12») y las **Unidades** que trae, como se ven en pantalla: «1.000» es mil y «0,5» es media.', 'Pulsa **Agregar** y ponle su precio por unidad. Vender una caja de 12 descuenta 12 del inventario.'],
      },
      {
        tipo: 'lista',
        titulo: 'Si fijas precios',
        permiso: FIJA_PRECIOS,
        items: [
          '**Inactivar** / **Reactivar** quitan o devuelven una presentación a la venta. La de 1 unidad no se inactiva: es la base del precio y del inventario.',
          'Si una presentación mayor queda más cara por unidad que una menor, el sistema avisa, pero guarda.',
          '**Editar ficha** cambia los datos del producto. Guarda aunque su categoría, su subcategoría o su pieza suelta se hayan desactivado después; lo que no se puede es elegirle otra desactivada.',
          '**Desactivar** lo saca de la venta sin borrarlo; **Activar** lo devuelve. **Desactivar** pide confirmar en una ventana: deja de aparecer en el punto de venta, en las cotizaciones y en las búsquedas, y su historial, sus precios y sus documentos quedan igual. **Activar** es directo. Un producto con existencia, material por entregar, traslados en camino, lotes a la venta o ajustes por aprobar no se desactiva: el aviso «No se pudo desactivar» dice qué tiene y qué hacer primero.',
          'El **Histórico de precios** muestra cada cambio con el precio anterior, el nuevo y quién lo hizo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Stock',
        items: [
          'Cada almacén de tu sucursal: existencia (**Exist.**), pendiente por entregar (**Pend.**), disponible (**Dispon.**) y **Mínimo**, con su estado: **bajo mínimo** o **cerca del mínimo**.',
          'Debajo, lo disponible en las otras sucursales, como referencia.',
          'Un servicio no lleva inventario: en vez del stock, la ficha dice que se factura como cualquier renglón y nace entregado.',
          'Al final dice en qué fracción se vende, la medida de la lámina (si la tiene) y su despiece (si es frágil).',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Mínimos (dueña)',
        permiso: { clave: 'productos.minimos' },
        items: [
          '**Mínimo por sucursal**: operando la sucursal, escribe el número en ese recuadro y pulsa **Fijar** («1.000» es mil). Alimenta la estimación de compra.',
          '**Mínimo por almacén**: en la tabla de stock, escribe el número en la columna **Mínimo** de ese almacén y pulsa Enter (o sal de la casilla). Vacío o 0 es sin alerta.',
          '**Bajo mínimo**: lo disponible es menor que el mínimo. **Cerca del mínimo**: lo supera por menos del 20 % (el porcentaje está en los parámetros de **Configuración**).',
          'El mínimo se mide contra lo disponible: un producto puede salir bajo mínimo aunque siga en el estante, porque ya está vendido.',
          'Cuando una venta, una entrega, un ajuste o un traslado deja lo disponible por debajo del mínimo (del almacén o de la sucursal), los administradores de la sucursal y la dueña reciben el aviso «… bajó del mínimo». Sale solo al cruzarlo: si ya estaba bajo, no se repite.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Costo real',
        permiso: { clave: 'productos.costos' },
        items: [
          'No se escribe a mano: lo mueven las compras y sus gastos.',
          '**Costo de factura** de la compra vigente, **Gastos con factura / u** y **Gastos sin factura / u**.',
          'El detalle de cada gasto: de qué compra o gasto viene, concepto, monto, entre cuántas unidades y cuánto por unidad. Los de una compra anterior salen en gris: no suman hoy.',
          'El margen contra cada lista de precios y, al pulsar **Histórico del costo**, cada cambio del costo.',
        ],
      },
      { tipo: 'nota', texto: 'Un producto con existencia o con material por entregar no puede pasar a servicio. Primero lleva su existencia a cero con un ajuste.', permiso: FIJA_PRECIOS },
    ],
    mensajes: [
      { mensaje: 'Precio no decreciente', que_hacer: 'Una presentación mayor quedó más cara por unidad que una menor. Se guardó igual: revisa si es lo que querías.' },
      { mensaje: 'Ya existe una presentación de … unidades', que_hacer: 'Esa presentación ya está: cámbiale el precio en su fila.' },
      { mensaje: 'Este producto tiene existencia o material por entregar: no puede pasar a servicio', que_hacer: 'Lleva primero su existencia a cero con un ajuste.' },
      { mensaje: '… no se desactiva todavía: tiene existencia en … almacén(es), …', que_hacer: 'Entrega lo vendido, lleva su existencia a cero (venta, traslado o ajuste), da salida a sus lotes y resuelve sus ajustes y traslados; después desactívalo.' },
    ],
    manual: '14.3',
    relacionados: ['productos', 'inventario', 'ajustes', 'compras'],
    claves: ['ficha', 'presentación', 'precio por lista', 'histórico de precios', 'mínimo', 'costo real', 'margen', 'desactivar'],
  },

  // ─── Entregas ───────────────────────────────────────────────────────────────
  {
    id: 'entregas',
    titulo: 'Entregas',
    rutas: ['/entregas'],
    permiso: { clave: 'inventario.entregas' },
    resumen: 'La mercancía vendida queda apartada hasta que el cliente la retira. Aquí se registra lo que sale, completo o en partes, y se imprime la hoja de despacho.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Tres pestañas: **Por entregar** (con cuántas ventas hay), **Historial** y **Órdenes de retiro** (retiro en otra sucursal).',
          'Las fechas de arriba (**Hoy** pone el día) y el buscador de documento o cliente sirven para las dos pestañas.',
          'El inventario baja cuando se registra la entrega, no al vender.',
          'Lo registra quien tiene el permiso de entregas: el cajero, el administrador o almacén.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Retiro en varias veces',
        items: [
          'El cliente puede llevarse una parte y volver por el resto: cada retiro es una entrega.',
          'Si ya registraste la entrega y al final no se lleva todo (no le cupo en el vehículo, se lleva 3 de 5), usa **Dejar pendiente** el mismo día: vuelve al almacén y queda apartado para él. Se imprime una constancia que firma el cliente.',
          'Después de cada entrega que deja algo pendiente, de **Dejar pendiente** y al completar el retiro, el cliente recibe solo, en el correo de su ficha, el **Estado de su retiro**: lo comprado, lo retirado y lo pendiente, sin precios.',
          'Si el cliente no tiene correo, **Copiar mensaje para WhatsApp** copia el mismo texto para enviárselo por WhatsApp.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Retiro en otra sucursal',
        items: [
          'El cliente compra aquí y retira en la otra sucursal: **Otra sucursal** en **Por entregar** hace la orden de retiro. La autoriza la dueña (o a quien ella le dio el permiso); si no, se pide la autorización.',
          'Lo indicado queda apartado allá y el cliente recibe un código de 6 dígitos. En la otra tienda le piden el código y la cédula.',
          'En la sucursal de retiro, **Para retirar aquí desde otra sucursal** (arriba de **Por entregar**) tiene las órdenes que le tocan.',
          'La orden vale 7 días. Se extiende con justificación o se anula con motivo; al vencer o anularse, lo apartado vuelve aquí.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Material sin retirar',
        items: [
          'Pasado el plazo de retiro (5 días hábiles al instalar), la venta sale en rojo: «… días hábiles sin retirar».',
          'Se avisa una sola vez a la administración de la tienda y a la dueña, y el cliente recibe una vez el **Estado de su retiro** por correo.',
          'A los 10 días hábiles sin retirar, la dueña recibe un aviso para decidir qué hacer. Lo apartado sigue apartado: no vence.',
          'Los avisos salen al abrir esta pantalla o el consolidado y, además, solos cada hora en horario de tienda.',
          'Los días hábiles son de lunes a viernes; el sistema no conoce los feriados.',
        ],
      },
    ],
    mensajes: [{ mensaje: '… venta(s) con material sin retirar pasado el plazo: administración y dueña avisadas', que_hacer: 'Es solo un aviso. Esas ventas salen en rojo en **Por entregar**.' }],
    manual: '15',
    relacionados: ['pos', 'documentos', 'impresion', 'correo'],
    claves: ['entregar', 'despacho', 'retiro', 'apartado', 'pendiente', 'hoja de despacho', 'sin retirar', 'dejar pendiente', 'estado de su retiro', 'whatsapp'],
  },
  {
    id: 'entregas.pendientes',
    padre: 'entregas',
    titulo: 'Pestaña Por entregar',
    resumen: 'Las ventas con mercancía pendiente: cuánto falta, desde cuándo y de qué almacén se retira.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Documento**, **Cliente** y la fecha de emisión (**Emitido**).',
          '**Antigüedad** en días: en ámbar desde el tercer día; en rojo, pasado el plazo de retiro, con los días hábiles sin retirar.',
          '**Unid. pendientes** y, debajo, el almacén donde está apartada (tienda o depósito).',
          '**Estado**: **Sin entregar** o **Parcial**, y **Dejado pendiente** si el cliente dejó algo de una entrega.',
          '**Antigüedad** dice también los días hábiles desde la compra.',
          '**Aviso al cliente**: **Correo** con la fecha si ya se le envió el estado de su retiro, **Correo no salió** si el servicio de correo no lo aceptó, o **Sin correo**.',
          '**WhatsApp** copia el estado del retiro en texto, para pegarlo en el chat del cliente.',
          'Los servicios no salen: no hay nada que entregar.',
          'Trae las 50 ventas más antiguas. Si hay más, al pie lo dice con el total: usa el buscador o las fechas para ver el resto.',
          '**Retira en … · número**: la venta tiene una orden de retiro en otra sucursal. Lo que está en la orden se entrega allá, no aquí.',
          'Arriba, **Para retirar aquí desde otra sucursal**: lo que viene a retirar aquí gente que compró en la otra sucursal (si hay).',
        ],
      },
      { tipo: 'pasos', titulo: 'Entregar', pasos: ['Pulsa **Entregar** en la venta.', 'Llena la ventana de entrega (tiene su propia ayuda) y pulsa **Registrar entrega**.'] },
      { tipo: 'pasos', titulo: 'Retirar en otra sucursal', pasos: ['Pulsa **Otra sucursal** en la venta.', 'Llena la ventana de la orden de retiro (tiene su propia ayuda) y pulsa **Hacer la orden de retiro**.', 'Dale al cliente el código que sale en pantalla: no se vuelve a mostrar.'] },
    ],
    manual: '15.1',
    claves: ['por entregar', 'antigüedad', 'parcial'],
  },
  {
    id: 'entregas.historial',
    padre: 'entregas',
    titulo: 'Pestaña Historial',
    resumen: 'Cada entrega registrada, con su documento, almacén, fecha y quién la hizo.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Columnas: **Entrega**, **Documento**, **Cliente**, **Almacén**, **Fecha** y **Por** (quién la registró).',
          '**Imprimir** vuelve a sacar la hoja de despacho de esa entrega. Cada vez cuenta como una impresión y queda en la auditoría; desde la segunda, la hoja dice **REIMPRESIÓN** junto al título.',
          'Usa las fechas y el buscador de arriba para encontrar una.',
          'Trae las 50 entregas más recientes. Si hay más, al pie lo dice con el total.',
          'En las entregas de hoy sale **Dejar pendiente**, para lo que el cliente al final no se llevó.',
          'Un retiro hecho con una orden de otra sucursal dice debajo del documento **Venta de …** (en la sucursal que despachó) o **Retirada en …** (en la de venta). Ese no se deja pendiente.',
        ],
      },
    ],
    manual: '15.3',
    relacionados: ['impresion'],
    claves: ['historial de entregas', 'reimprimir despacho'],
  },
  {
    id: 'entregas.entregar',
    padre: 'entregas',
    titulo: 'Registrar una entrega',
    resumen: 'Indica de qué almacén sale, quién recibe y cuánto se lleva de cada renglón. Lo que no entregues queda pendiente.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar',
        pasos: [
          'En **Por entregar**, pulsa **Entregar** en la venta.',
          '**Almacén que despacha**: viene el almacén donde está apartada la mercancía (si está en varios, la tienda). Cámbialo si despacha otro.',
          '**Recibe**: viene con el nombre del cliente. Cámbialo por el de quien retira.',
          'Si hace falta, escribe la **Cédula de quien retira** y las **Observaciones** (placa del vehículo, chofer…). Son opcionales y salen en la hoja de despacho.',
          '**Entregar ahora** viene con todo lo pendiente. Baja la cantidad del renglón que sale en parte, o pon 0 en el que no sale.',
          'Pulsa **Registrar entrega**. La hoja de despacho se abre sola en otra pestaña.',
          'En la ventana que queda, **Imprimir** la saca de nuevo y **Listo** la cierra. Dice si se le envió al cliente el estado de su retiro.',
          'Si al final no se lleva todo, pulsa **Dejar pendiente** en esa misma ventana. **Copiar mensaje para WhatsApp** copia el estado del retiro.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Cada renglón dice «apartado en…». Si despacha otro almacén, ese aviso sale en ámbar y la existencia sale del almacén que despacha.',
          'La cantidad respeta la fracción mínima del producto, como al vender: de lo que se vende entero no se entrega una parte. Lo que falta de un renglón siempre se puede entregar completo.',
          'En un depósito no hace falta un equipo: el retiro se anota desde la tienda, eligiendo ese depósito.',
          'Una pieza de vidrio o un producto con detalle se entrega como cualquier renglón, sin mover existencia: ya salió al aprobar el reporte.',
          'La hoja sale en media carta vertical o en tiquera de 80 mm, según tu caja. Dice lo vendido, lo que sale y lo que queda pendiente.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Factura con la retención por recibir',
        items: [
          'Si el cliente paga base + 25 % del IVA y su retención (de IVA o municipal) sigue «Por recibir», la venta dice **Retención por recibir** y la ventana lo avisa en rojo.',
          'El cajero y almacén necesitan la autorización de un administrador o de la dueña, con su clave o a distancia, para entregar completa o en parte. Cada entrega pide la suya.',
          'El administrador y la dueña entregan igual: queda anotado, y si es un administrador la dueña recibe el aviso.',
          'Con el comprobante cargado («Recibida»), o si el cliente pagó completo, se entrega como cualquier otra.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Si sale «No se pudo entregar» por un corte de conexión, pulsa **Registrar entrega** otra vez sin cambiar nada: la entrega no se registra dos veces ni descuenta el inventario dos veces.' },
    ],
    mensajes: [
      { mensaje: 'No se pudo entregar', que_hacer: 'Lee el motivo debajo. Si fue la conexión, pulsa **Registrar entrega** otra vez sin cambiar nada.' },
      { mensaje: 'Indica qué entregar', que_hacer: 'Escribe al menos una cantidad en **Entregar ahora**.' },
      { mensaje: '…: se intenta entregar … pero solo quedan … pendientes', que_hacer: 'Baja la cantidad a lo pendiente.' },
      { mensaje: 'Existencia insuficiente en el almacén: hay … y se descargan …', que_hacer: 'Ese almacén no tiene la mercancía: elige en **Almacén que despacha** el que sí la tiene.' },
      { mensaje: '… ya fue entregado por completo', que_hacer: 'Esa venta ya no tiene nada pendiente.' },
      { mensaje: '… se entrega en múltiplos de …: … no lo es', que_hacer: 'Corrige la cantidad a un múltiplo de su fracción, o entrega completo lo que falta del renglón.' },
      REINTENTO,
    ],
    manual: '15.2',
    relacionados: ['impresion'],
    claves: ['entregar', 'entrega parcial', 'recibe', 'almacén que despacha', 'depósito', 'despacho'],
  },

  {
    id: 'entregas.dejar-pendiente',
    padre: 'entregas',
    titulo: 'Dejar pendiente',
    resumen: 'Cuando ya registraste la entrega y el cliente al final no se lleva todo, lo que no se lleva vuelve al almacén y queda apartado para él. Solo el mismo día de la entrega.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Dejar pendiente',
        pasos: [
          'Pulsa **Dejar pendiente** en la ventana de la entrega recién registrada, o en el **Historial**, en la entrega de hoy.',
          'En **Queda pendiente** escribe cuánto no se lleva de cada producto. Cada renglón dice hasta cuánto se puede.',
          'Elige el **Motivo**: «No cupo en el vehículo», «Retira después» u **Otro** (y escríbelo).',
          'Pulsa **Dejar pendiente**.',
          'En la ventana que queda, **Imprimir constancia** saca la «Constancia de pendiente por retirar» para que el cliente la firme. **Copiar mensaje para WhatsApp** copia el estado del retiro.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Vuelve a la existencia del almacén que despachó y queda apartado otra vez para el cliente: nadie más lo puede vender.',
          'La venta vuelve a **Parcial** (o a **Sin entregar** si no se llevó nada) y sale otra vez en **Por entregar**, marcada **Dejado pendiente**.',
          'No es una devolución: no hay nota de crédito, no se mueve dinero y la factura no cambia.',
          'Solo el mismo día de la entrega. Si el cliente trae mercancía de otro día, es una devolución (**Devoluciones**).',
          'Nunca más de lo que salió en esa entrega. Si una parte ya se devolvió, esa parte no se deja pendiente.',
          'El cliente recibe solo, en el correo de su ficha, el estado de su retiro con lo que le queda pendiente.',
          'La constancia sale en media carta vertical o en tiquera de 80 mm, según tu caja. Desde la segunda impresión dice **REIMPRESIÓN**.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'La entrega … es del …: «Dejar pendiente» solo se usa el mismo día de la entrega…', que_hacer: 'Pasado el día, lo que el cliente trae de vuelta se registra en **Devoluciones**.' },
      { mensaje: '…: en la entrega … salieron …; se pueden dejar pendientes hasta …', que_hacer: 'Baja la cantidad a lo que dice el mensaje.' },
      { mensaje: '…: ya se devolvieron … de lo entregado…', que_hacer: 'Lo devuelto ya volvió al inventario con su nota de crédito: deja pendiente solo lo demás.' },
      { mensaje: '…: estaba apartado en otro almacén y ese almacén ya no tiene disponible para apartarlo otra vez…', que_hacer: 'Registra la entrega desde ese almacén o pide un traslado.' },
      { mensaje: 'No se pudo dejar pendiente', que_hacer: 'Lee el motivo debajo. Si fue la conexión, pulsa **Dejar pendiente** otra vez sin cambiar nada: no se registra dos veces.' },
    ],
    manual: '15.6',
    relacionados: ['entregas.entregar', 'devoluciones', 'correo'],
    claves: ['dejar pendiente', 'no cupo', 'retira después', 'constancia', 'pendiente por retirar', 'se lleva una parte'],
  },
  {
    id: 'entregas.ordenes-retiro',
    padre: 'entregas',
    titulo: 'Pestaña Órdenes de retiro',
    resumen: 'Las órdenes de retiro en otra sucursal: las de ventas de aquí que se retiran en la otra y las que se retiran aquí, con su estado.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Orden**, la **Venta** (con su sucursal), el **Cliente**, dónde **Retira en**, el **Estado**, cuándo **Vence** y lo retirado de lo autorizado.',
          'Estados: **Activa** (sin retirar), **Parcial** (retiró una parte), **Retirada** (todo), **Vencida** (pasaron los días y lo apartado volvió) y **Anulada**.',
          '**Código bloqueado**: se equivocaron 5 veces con el código o la cédula. La sucursal de venta da un **Código nuevo**.',
          'Filtra por **Vendidas aquí** o **Para retirar aquí**, y por estado.',
          'Los botones con el número de entrega vuelven a sacar el despacho de cada retiro.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Lo que hace la sucursal de venta',
        items: [
          '**Extender**: suma 7 días más, con una justificación.',
          '**Código nuevo**: el anterior deja de servir. El nuevo sale una vez en pantalla y va al correo del cliente.',
          '**Anular**: con el motivo. Lo que falta por retirar vuelve aquí, apartado para el cliente.',
          'Las tres las hace la dueña o quien tenga su permiso; los demás piden autorización (con la clave de quien la da o a distancia).',
        ],
      },
      { tipo: 'nota', texto: 'La sucursal de retiro solo registra el retiro: no extiende, no anula ni da códigos.' },
    ],
    mensajes: [
      { mensaje: '…: en … ya no hay disponible para apartar otra vez … (se vendió mientras tanto). La orden … sigue abierta…', que_hacer: 'Lo que estaba apartado aquí se vendió: que el cliente retire en la otra sucursal (extiende la orden si venció) o repón la existencia.' },
      { mensaje: 'La orden de retiro … venció el …: extiéndela antes de dar un código nuevo', que_hacer: 'Pulsa **Extender** y después **Código nuevo**.' },
    ],
    manual: '15.8',
    relacionados: ['entregas.orden-retiro', 'entregas.retiro-aqui', 'autorizacion'],
    claves: ['orden de retiro', 'otra sucursal', 'retiro en otra sucursal', 'extender', 'anular orden', 'código nuevo', 'vencida'],
  },
  {
    id: 'entregas.orden-retiro',
    padre: 'entregas',
    titulo: 'Retirar en otra sucursal (orden de retiro)',
    resumen: 'El cliente compró aquí y retira en la otra sucursal: lo indicado queda apartado allá y el cliente recibe un código de retiro.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Hacer la orden',
        pasos: [
          'En **Por entregar**, pulsa **Otra sucursal** en la venta.',
          'En **Retira en** elige la sucursal y el almacén. Cada renglón dice cuánto hay **Disponible allá**; en rojo si no alcanza.',
          'En **Retira allá** deja o baja la cantidad de cada producto (nunca más de lo pendiente).',
          'Si ya sabes quién va a retirar, escribe su nombre y su cédula. Si pones la cédula, en la otra tienda se exige esa.',
          'Pulsa **Hacer la orden de retiro**. Si tu usuario no tiene el permiso, se pide la autorización de la dueña (con su clave o a distancia).',
          'Sale el **Código de retiro** en grande. Dáselo al cliente: no se vuelve a mostrar.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa',
        items: [
          'En una sola operación, lo indicado queda apartado en el almacén de la otra sucursal y deja de estar apartado aquí. Nadie allá le vende eso a otro.',
          'El cliente recibe en el correo de su ficha «Retiro autorizado en …» con la dirección, los productos, el código y hasta cuándo vale. El código no se guarda en el sistema (solo sirve para comprobarlo).',
          'La orden vale 7 días. Una venta tiene una sola orden abierta a la vez.',
          'Lo que quedó en la orden ya no se entrega aquí. Para entregarlo aquí, anula la orden.',
          'Una venta a crédito se retira en la otra sucursal igual que aquí.',
          'Si la crea otro con el permiso o con una autorización, la dueña recibe el aviso. La otra sucursal también.',
        ],
      },
    ],
    mensajes: [
      { mensaje: '…: en … hay … disponibles y la orden pide …', que_hacer: 'Allá no alcanza: baja la cantidad o elige otro almacén.' },
      { mensaje: '… ya tiene la orden de retiro … para retirar en …: anúlala para hacer otra', que_hacer: 'En **Órdenes de retiro**, anula la que está y haz una nueva.' },
      { mensaje: '… es de esta misma sucursal: para retirar aquí usa «Entregar»…', que_hacer: 'Elige un almacén de la otra sucursal. Para un depósito de aquí, usa **Entregar** y elige ese depósito.' },
      { mensaje: '… es una pieza de vidrio, un producto con detalle o un servicio…', que_hacer: 'Eso no lleva existencia: se entrega aquí.' },
      { mensaje: 'No se pudo hacer la orden de retiro', que_hacer: 'Lee el motivo debajo. Si fue la conexión, pulsa otra vez sin cambiar nada: no se hace dos veces.' },
    ],
    manual: '15.8',
    relacionados: ['entregas.ordenes-retiro', 'entregas.retiro-aqui', 'autorizacion', 'correo'],
    claves: ['orden de retiro', 'otra sucursal', 'código de retiro', 'retirar en barinas', 'retirar en barquisimeto'],
  },
  {
    id: 'entregas.retiro-aqui',
    padre: 'entregas',
    titulo: 'Registrar un retiro de otra sucursal',
    resumen: 'El cliente compró en la otra sucursal y viene a retirar aquí con su código: se pide el código y la cédula, y sale el despacho con la guía de traslado.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar el retiro',
        pasos: [
          'En **Por entregar**, en **Para retirar aquí desde otra sucursal**, pulsa **Registrar retiro** en la orden.',
          'Escribe el **Código de retiro** que trae el cliente (6 dígitos) y la **Cédula de quien retira**. Si la orden dice quién está autorizado, debe ser esa cédula.',
          'En **Retira ahora** deja o baja lo que se lleva. Puede ser una parte.',
          'Pulsa **Registrar retiro**.',
          'En la ventana que queda, **Imprimir despacho** y **Imprimir guía de traslado**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa',
        items: [
          'En una sola operación: una guía de traslado de aquí a la sucursal de venta (al costo, sin factura, recibida en el acto) y la entrega contra la factura o nota de la otra sucursal.',
          'La existencia baja aquí. En la sucursal de venta entra y sale con la guía: allá queda igual. Lo pendiente de la venta baja.',
          'El despacho dice «Factura N.º … de …» y, debajo, «retirada en …».',
          'El código es de un solo uso. Si queda algo por retirar, el cliente recibe un código nuevo en su correo (aquí no se muestra); si no tiene correo, se lo da la sucursal de venta.',
          'El cliente recibe el estado de su retiro en su correo. La dueña recibe el aviso de cada retiro en otra sucursal.',
          'Una factura «base + 25 %» con la retención por recibir pide la autorización de la dueña o de un administrador, como cualquier entrega.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Cada código o cédula equivocados cuentan. A los 5 intentos el código se bloquea y la sucursal de venta tiene que dar uno nuevo.' },
    ],
    mensajes: [
      { mensaje: 'El código de retiro no coincide: quedan … intentos.', que_hacer: 'Pídele al cliente el código del último correo «Retiro autorizado» o «Código nuevo». Si no lo tiene, llama a la sucursal de venta.' },
      { mensaje: 'La cédula no coincide con la de la persona autorizada a retirar…', que_hacer: 'Solo retira la persona que dice la orden. Si es otra, la sucursal de venta debe hacer otra orden.' },
      { mensaje: 'El código de la orden … está bloqueado…', que_hacer: 'La sucursal de venta da un **Código nuevo** desde **Órdenes de retiro**.' },
      { mensaje: 'La orden de retiro … venció el …', que_hacer: 'La sucursal de venta puede extenderla. Mientras tanto no se retira.' },
    ],
    manual: '15.8',
    relacionados: ['entregas.orden-retiro', 'traslados.guia', 'impresion'],
    claves: ['retiro de otra sucursal', 'código de retiro', 'para retirar aquí', 'guía de traslado', 'cédula'],
  },

  // ─── Inventario ─────────────────────────────────────────────────────────────
  {
    id: 'inventario',
    titulo: 'Inventario',
    rutas: ['/inventario'],
    permiso: { clave: 'inventario.conteos', nivel: 'propone' },
    resumen: 'El stock de cada producto en cada almacén de tu sucursal, con su kardex, los conteos físicos, el despiece de cajas y la carga de la existencia inicial.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Pestañas **Stock** y **Conteos**.',
          '**Existencia inicial (Excel)**: carga lo que hay de los productos que nunca han tenido existencia en un almacén. Lo ve quien propone o aprueba ajustes.',
          '**Despiece de cajas**: convierte cajas rotas en piezas sueltas.',
          'Las roturas de vidrio y los productos con detalles tienen su propio menú: **Roturas y detalles**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Almacenes',
        items: [
          'Cada sucursal tiene una **tienda** (su almacén principal, de donde sale la venta normal) y uno o más **depósitos**.',
          'Barquisimeto: Tienda y Depósito Cabudare. Barinas: Tienda, Depósito 1 y Depósito 2.',
          '**Disponible** es la existencia menos lo vendido que aún no se entrega.',
        ],
      },
    ],
    manual: '16',
    relacionados: ['ajustes', 'roturas', 'traslados', 'entregas'],
    claves: ['stock', 'existencia', 'almacén', 'depósito', 'kardex', 'conteo', 'despiece'],
  },
  {
    id: 'inventario.stock',
    padre: 'inventario',
    titulo: 'Pestaña Stock',
    resumen: 'Cuánto hay de cada producto en cada almacén y cada movimiento que tuvo.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Por producto y almacén, con su tienda: **Existencia** (lo que hay), **Apartado (por entregar)** (vendido con factura o nota que falta por entregar, o apartado para un retiro en esta tienda), **Disponible** (existencia − apartado, lo que se puede vender) y **Mínimo**.',
          'Pulsa el número del **Apartado** (en ámbar) para ver a quién está: cada venta por entregar y cada orden de retiro, con el total.',
          'El **Disponible** sale en verde (en rojo si queda en negativo) y con la marca **bajo mínimo** si no llega al mínimo del almacén.',
          'Un producto en varios almacenes lleva su **Total**; con varias tiendas, además el **Subtotal** de cada una.',
          'La dueña (y la consola) elige **Tienda**: la que opera, la otra o **Todas las tiendas**. Es solo para ver; los demás usuarios ven su tienda.',
          'Filtros: **Buscar producto…**, **Almacén**, **Solo con existencia o apartado** (viene marcada) y **Solo con apartado**.',
          'La columna **Valor (costo)** la ve solo quien tiene el permiso de costos.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Ver el kardex de un producto',
        pasos: [
          'Pulsa **Kardex** en la fila del producto.',
          'Sale cada movimiento: fecha, almacén, tipo (con su nombre: «Compra», «Entrega»…), referencia (la venta, la compra, la guía, el ajuste…), la cantidad que entró o salió, la existencia y el pendiente después, y quién lo hizo.',
          'Lo que entra por un traslado dice qué fue: «Traslado: recibido» en el destino, «Traslado: faltante que vuelve» o «Traslado: cancelado (vuelve)» en el origen.',
          'Quien ve costos tiene además la columna **Valor Bs**: el valor en bolívares de cada movimiento de compra.',
          'Lo que entra sale en verde y lo que sale, en rojo.',
          'Muestra los 200 movimientos más recientes. Arriba dice «La suma reconstruye el stock.» solo si están todos; si hay más, avisa que la suma de esos ya no da toda la existencia.',
        ],
      },
      { tipo: 'nota', texto: 'El stock no se edita a mano: para corregirlo se hace un conteo o un ajuste.' },
    ],
    manual: '16.1',
    relacionados: ['ajustes'],
    claves: ['stock', 'existencia', 'pendiente', 'disponible', 'kardex', 'movimientos', 'valor'],
  },
  {
    id: 'inventario.conteos',
    padre: 'inventario',
    titulo: 'Pestaña Conteos',
    permiso: CONTEOS,
    resumen: 'Compara lo que dice el sistema con lo que hay en el estante, almacén por almacén. Las diferencias pasan a un ajuste que aprueba la dueña.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Abrir un conteo',
        pasos: [
          'Elige el almacén en **Almacén a contar** y pulsa **Nuevo conteo**.',
          'El sistema toma una foto de lo que tiene registrado en ese almacén y abre el conteo para escribir lo físico.',
          'Para volver a un conteo abierto, pulsa **Capturar físico** en su fila. Uno confirmado se abre con **Ver**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Cada conteo con su almacén, su estado (**borrador** o **confirmado**), quién lo creó y el ajuste que generó.',
          'Solo puede haber un conteo abierto (en borrador) por almacén, aunque dos personas pulsen **Nuevo conteo** a la vez.',
          'Salen los productos que ya tienen existencia registrada en ese almacén. La primera existencia entra con **Existencia inicial (Excel)** o con una compra.',
        ],
      },
    ],
    mensajes: [{ mensaje: 'Ya hay un conteo en borrador para este almacén: confírmalo antes de abrir otro', que_hacer: 'Ábrelo con **Capturar físico** y confírmalo.' }],
    manual: '16.2',
    relacionados: ['ajustes'],
    claves: ['conteo', 'inventario físico', 'contar', 'diferencia'],
  },
  {
    id: 'inventario.conteo',
    padre: 'inventario',
    titulo: 'Capturar y confirmar un conteo',
    permiso: CONTEOS,
    resumen: 'Escribe la cantidad física de cada producto y confirma. Si hay diferencias, se crea un ajuste «Diferencia de conteo» que queda pendiente.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Contar',
        pasos: [
          'En **Físico** escribe lo que contaste de cada producto. La **Diferencia** se calcula mientras escribes.',
          '**Guardar físico** guarda lo escrito y deja igual al sistema lo que no contaste. Sirve para seguir después.',
          '**Confirmar conteo** guarda lo escrito y cierra el conteo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Al confirmar',
        items: [
          'Sin diferencias, el conteo se cierra y no pasa nada más.',
          'Con diferencias, se crea un ajuste **Diferencia de conteo** que queda pendiente y la dueña recibe el aviso «El conteo de … dejó el ajuste … por aprobar». El inventario se mueve cuando lo aprueba quien aprueba ajustes (la dueña), en **Ajustes**.',
          'Antes de cerrar, revisa producto por producto si la existencia sigue siendo la de la foto. Si alguno se movió después de abrir el conteo (una venta, una entrega, un ajuste), dice cuántos productos son y pregunta **¿Confirmar de todas formas?**. Acepta solo si lo que contaste sigue valiendo. El aviso también sale arriba, en la ventana.',
          'Si **Confirmar conteo** se pulsa dos veces o desde dos pantallas, sale un solo ajuste.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Un conteo confirmado ya no se cambia. Revisa las cifras antes de confirmar.' },
    ],
    mensajes: [
      { mensaje: 'Faltan … producto(s) sin cantidad física', que_hacer: 'Escribe todas las cantidades, o pulsa **Guardar físico** para igualar al sistema lo que no contaste.' },
      { mensaje: 'Hubo movimientos en el almacén desde que se cargó el conteo: … producto(s) ya no tienen la existencia de la foto. Revisa y confirma con "forzar" si las cifras físicas siguen siendo válidas.', que_hacer: 'Esos productos se vendieron, se entregaron o se ajustaron después de abrir el conteo. Revísalos; si tus cifras siguen valiendo, acepta **¿Confirmar de todas formas?**.' },
      { mensaje: '… ya está confirmado', que_hacer: 'Otra persona, u otro clic, ya confirmó el conteo: salió un solo ajuste. Actualiza la pantalla.' },
    ],
    manual: '16.2',
    relacionados: ['ajustes'],
    claves: ['capturar físico', 'guardar físico', 'confirmar conteo', 'diferencia de conteo'],
  },
  {
    id: 'inventario.despiece',
    padre: 'inventario',
    titulo: 'Despiece de cajas',
    permiso: CONTEOS,
    resumen: 'Para una caja rota de la que se salvan piezas (por ejemplo porcelanato): salen las cajas, entran las piezas sueltas y lo que falta queda como merma.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un despiece',
        pasos: [
          'En **Inventario**, pulsa **Despiece de cajas**.',
          'Elige el **Almacén** y el **Producto (caja)**.',
          'Escribe las **Cajas rotas** y las **Piezas recuperadas**. No pueden ser más de las que traen esas cajas.',
          'Escribe la **Justificación** (al menos 5 letras) y pulsa **Registrar despiece**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Cuándo se aplica',
        items: [
          '**Sin merma** (salen todas las piezas): es solo un cambio de caja a piezas y se aplica de inmediato, lo registre quien lo registre.',
          '**Con merma**, si lo registra quien aprueba ajustes (la dueña): se aplica de inmediato.',
          '**Con merma**, si lo registra otra persona: queda por aprobar y la existencia no cambia todavía. La dueña recibe el aviso y lo aprueba o lo rechaza en **Ajustes**.',
          'Solo sirve para productos frágiles con su **Producto pieza suelta** y sus **Piezas por caja** en la ficha. Ni la caja ni su pieza suelta pueden estar desactivadas.',
          'En **Ajustes**, el **Valor** de un despiece es su merma: lo que sale menos lo que entra.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Si sale «No se pudo despiezar» por un corte de conexión, pulsa **Registrar despiece** otra vez sin cambiar nada: no se registra dos veces.' },
    ],
    mensajes: [
      { mensaje: 'Despiece … registrado: la merma queda por aprobar', que_hacer: 'Espera a que la dueña lo apruebe en **Ajustes**. Hasta entonces la existencia no cambia.' },
      { mensaje: '… no tiene despiece configurado (producto destino y factor)', que_hacer: 'A ese producto le falta su pieza suelta o sus piezas por caja. La dueña lo completa en la ficha, con **Editar ficha**.' },
      { mensaje: 'De … caja(s) salen como máximo … piezas', que_hacer: 'Corrige las **Piezas recuperadas**.' },
      { mensaje: '… está desactivado: actívalo en Productos antes de despiezarlo · La pieza suelta de … (…) está desactivada: actívala en Productos antes de despiezar', que_hacer: 'Pídele a la dueña que active la caja o su pieza suelta en su ficha, y despieza después.' },
      { mensaje: 'Existencia insuficiente en el almacén: hay … y se descargan …', que_hacer: 'Ese almacén no tiene esas cajas: revisa el almacén o la cantidad.' },
      REINTENTO,
    ],
    manual: '16.3',
    relacionados: ['ajustes', 'productos'],
    claves: ['despiece', 'caja rota', 'frágil', 'porcelanato', 'piezas sueltas', 'merma'],
  },
  {
    id: 'inventario.existencia-inicial',
    padre: 'inventario',
    titulo: 'Existencia inicial (Excel)',
    permiso: { clave: 'inventario.ajustes', nivel: 'propone' },
    resumen: 'Registra lo que hay en un almacén de los productos que nunca han tenido existencia ahí: al arrancar el sistema o cuando se crean productos nuevos.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cargar',
        pasos: [
          'En **Inventario**, pulsa **Existencia inicial (Excel)**. Hay que estar operando una sucursal.',
          'Elige el almacén en **Almacén…**.',
          'Pulsa **Descargar plantilla**. Trae los productos activos que nunca han tenido existencia en ese almacén, con su código, nombre y unidad. Los servicios no salen.',
          'Escribe en la columna **Cantidad** lo que hay de cada uno, en su unidad. Deja en blanco lo que no hay. No cambies los códigos ni los títulos; puedes borrar filas.',
          'Pulsa **Elegir archivo** y sube el Excel. El sistema revisa y muestra, fila por fila, qué entra y qué no.',
          'Pulsa **Cargar … productos**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa al cargar',
        items: [
          'Todo entra como un solo ajuste con motivo **Inventario inicial**.',
          'Si tu usuario aprueba ajustes (la dueña), la existencia entra de una vez. Si solo propone, la dueña recibe el aviso «… propuso el ajuste …» y la existencia entra cuando lo aprueben en **Ajustes**.',
          'El aviso dice «Carga lista: … productos en el ajuste» y, debajo, si el ajuste quedó aplicado o por aprobar. Si quedó por aprobar, cada fila dice «entran … cuando se apruebe el ajuste»: todavía no entró nada.',
          'Las filas sin cantidad no cuentan. Una cantidad 0 no entra.',
          'Un producto que ya tiene existencia en ese almacén se deja igual, y también el que ya está en un ajuste por aprobar. Por eso repetir la carga no suma dos veces.',
          'Un producto que ya tuvo existencia ahí y hoy está en cero tampoco entra: no es existencia inicial. Lo que entre va por una compra, un conteo o un ajuste.',
          'Cada archivo carga hasta 1000 productos con cantidad. Si son más, descarga la plantilla otra vez: ya no trae los que se cargaron.',
          'Cambiar de almacén limpia el archivo elegido.',
        ],
      },
      { tipo: 'nota', texto: 'El costo no entra por aquí: entra con la primera compra de cada producto. Mientras un producto no tenga existencia en la tienda, no se puede vender.' },
    ],
    mensajes: [
      { mensaje: 'Ya tiene existencia (…): se corrige con un conteo o un ajuste', que_hacer: 'Haz un conteo o un ajuste de ese producto.' },
      { mensaje: 'Ya está en un ajuste por aprobar de este almacén', que_hacer: 'Esa existencia ya se cargó: espera a que aprueben el ajuste.' },
      { mensaje: 'No es existencia inicial: ya tuvo existencia en este almacén (hoy está en cero). Lo que entre va por una compra, un conteo o un ajuste', que_hacer: 'Registra la compra, o haz un conteo o un ajuste de ese producto.' },
      { mensaje: 'El archivo no es un Excel (.xlsx)', que_hacer: 'Usa la plantilla del sistema.' },
      { mensaje: 'Al Excel le faltan las columnas …', que_hacer: 'Descarga la plantilla y llena esa.' },
    ],
    manual: '16.5',
    relacionados: ['ajustes'],
    claves: ['existencia inicial', 'inventario inicial', 'Excel', 'arranque', 'plantilla', 'cargar existencia'],
  },

  // ─── Ajustes ────────────────────────────────────────────────────────────────
  {
    id: 'ajustes',
    titulo: 'Ajustes de inventario',
    rutas: ['/ajustes'],
    permiso: { clave: 'inventario.ajustes', nivel: 'propone' },
    resumen: 'Todo movimiento de inventario que no sea venta, entrega, compra o traslado pasa por aquí, con su motivo y su justificación. Almacén y el administrador proponen; la dueña aprueba.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Filtra con el desplegable **Estado**: **Todos**, **Pendientes**, **Aprobados** o **Rechazados**.',
          'Cada ajuste con su número, su tipo y motivo (por ejemplo «conteo · Diferencia de conteo»), el almacén, quién lo propuso y cuándo, su **Valor** y su **Estado**.',
          'El **Valor** (a costo) lo ve solo quien tiene el permiso de costos. En un despiece, el valor es su merma: lo que sale menos lo que entra.',
          '**Revisar** abre un ajuste pendiente para aprobarlo o rechazarlo; **Ver** abre el detalle.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Ajustes que crea el sistema',
        items: [
          '**Inventario inicial**: al cargar la existencia inicial por Excel. Se aplica de una vez si lo carga quien aprueba ajustes; si no, queda pendiente y la dueña recibe el aviso.',
          '**Diferencia de conteo**: al confirmar un conteo con diferencias. Queda pendiente y la dueña recibe el aviso.',
          '**Despiece con merma**: cuando lo registra quien no aprueba ajustes. Queda pendiente y la dueña recibe el aviso.',
          '**Merma** de un traslado: cuando al recibir una guía se declara merma. Queda pendiente en la tienda de origen.',
          '**Daño**: cuando un cliente devuelve material dañado. Se aplica en el acto y la dueña recibe el aviso.',
        ],
      },
      { tipo: 'nota', texto: 'Los ajustes que registra quien aprueba (la dueña) se aplican de inmediato.' },
    ],
    manual: '17',
    relacionados: ['inventario', 'traslados', 'devoluciones', 'roturas'],
    claves: ['ajuste', 'merma', 'pérdida', 'daño', 'rotura', 'aprobar ajuste', 'pendiente'],
  },
  {
    id: 'ajustes.nuevo',
    padre: 'ajustes',
    titulo: 'Nuevo ajuste',
    resumen: 'Saca o mete mercancía en un almacén por una razón que no es venta, entrega, compra ni traslado. Si no apruebas ajustes, queda pendiente hasta que lo aprueben.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Proponer un ajuste',
        pasos: [
          'Pulsa **Nuevo ajuste**.',
          'Elige el **Almacén** y el **Motivo**: Rotura, Merma, Daño, Pérdida, Inventario inicial, Uso interno u Otro.',
          'Escribe la **Justificación**: qué pasó, cuándo y quién lo vio (al menos 5 letras).',
          'Busca cada producto en **Agregar producto…** y pulsa el que es. Los servicios no salen: no llevan existencia.',
          'En **Cantidad (±)** escribe negativa si sale y positiva si entra. Cada producto entra con -1.',
          'Pulsa **Registrar ajuste**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: ['Un producto desactivado no se ajusta: primero se activa en **Productos**.', '**Inventario inicial** solo suma existencia: la cantidad va en positivo.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa después',
        items: [
          'Si lo propone almacén o el administrador, queda **Pendiente**: no mueve nada todavía. La dueña recibe en la campana «… propuso el ajuste …».',
          'Cuando lo aprueban o lo rechazan, te llega «Ajuste … aprobado» o «Ajuste … rechazado», con el comentario si lo hubo.',
          'Si lo registra quien aprueba ajustes (la dueña), se aplica de inmediato y queda en el kardex.',
          'El botón de la papelera quita un producto antes de registrar.',
        ],
      },
      { tipo: 'nota', texto: '**Uso interno** es la mercancía que la tienda usa para sí misma (por ejemplo, pintura para el local): sale como autoconsumo en el libro de inventario. Solo saca mercancía, así que la cantidad va en negativo.' },
      {
        tipo: 'lista',
        titulo: 'Así queda en los libros fiscales',
        permiso: { clave: 'productos.costos', nivel: 'lectura' },
        items: [
          'Encima de **Registrar ajuste**, la vista previa dice cómo entra cada renglón al libro de inventario, antes de guardar.',
          '**Inventario inicial** que entra va en Entradas y es lo único que entra también al libro de inventario **fiscal**. Lo demás que entra, en Entradas · Sobrantes y ajustes; **Uso interno**, en Autoconsumos; Rotura, Merma, Daño y Pérdida, en Retiros.',
          'El ajuste de la dueña entra en el libro de este mes; el de los demás, el mes en que se apruebe.',
          'El inventario inicial entra con su valor: unidades × costo en $ × BCV. Si el producto no tiene costo (nunca se ha comprado), avisa que entra con Bs 0,00. Lo demás va al costo promedio del mes.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Si al registrar sale un error de conexión, pulsa **Registrar ajuste** otra vez sin cambiar nada: el ajuste no se registra dos veces.' },
    ],
    mensajes: [
      { mensaje: 'No se pudo registrar', que_hacer: 'Lee el motivo debajo. Si fue la conexión, pulsa **Registrar ajuste** otra vez sin cambiar nada.' },
      { mensaje: 'La cantidad no puede ser cero', que_hacer: 'Escribe una cantidad negativa (sale) o positiva (entra).' },
      { mensaje: 'Existencia insuficiente en el almacén: hay … y se descargan …', que_hacer: 'No se puede sacar más de lo que hay. Revisa la cantidad o el almacén.' },
      { mensaje: '… es un servicio: no lleva existencia ni se ajusta', que_hacer: 'Quita ese renglón del ajuste.' },
      { mensaje: '… está desactivado: actívalo en Productos antes de ajustar su existencia', que_hacer: 'Pídele a la dueña que lo active en su ficha y vuelve a registrar el ajuste.' },
      { mensaje: 'El inventario inicial solo suma existencia: para sacar mercancía usa otro motivo', que_hacer: 'Escribe la cantidad en positivo, o elige otro motivo si sale mercancía.' },
      REINTENTO,
    ],
    manual: '17.1',
    claves: ['nuevo ajuste', 'justificación', 'motivo', 'cantidad negativa'],
  },
  {
    id: 'ajustes.revisar',
    padre: 'ajustes',
    titulo: 'Revisar un ajuste',
    resumen: 'El detalle de un ajuste: motivo, justificación, productos y cantidades. Si está pendiente, la dueña lo aprueba o lo rechaza aquí.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Aprobar o rechazar (dueña)',
        permiso: APRUEBA_AJUSTES,
        pasos: [
          'En la lista, pulsa **Revisar** en el ajuste pendiente.',
          'Lee la justificación y revisa los productos y las cantidades.',
          'Escribe un **Comentario** si hace falta.',
          'Pulsa **Aprobar y aplicar** (el inventario se mueve y queda en el kardex) o **Rechazar** (no se mueve nada).',
          'El aviso «por aprobar» deja de esperar para todos, y quien lo propuso recibe «Ajuste … aprobado» o «Ajuste … rechazado», con el motivo y tu comentario.',
          'Nadie aprueba lo suyo, salvo la dueña: si la dueña te dio el permiso de aprobar ajustes, lo que tú propones (y el ajuste de un conteo que confirmaste) queda pendiente y lo aprueba otra persona con el permiso, o la dueña. La ventana lo dice y no muestra **Aprobar y aplicar**; puedes retirarlo con **Rechazar**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Quién lo propuso y cuándo, y quién lo resolvió.',
          'Cada producto con su **Cantidad** (en rojo lo que sale, en verde lo que entra) y la existencia antes y después de aplicarlo. Mientras está pendiente, dice «pendiente».',
          'El **Costo u.** y el **Valor del ajuste** los ve solo quien tiene el permiso de costos.',
          'En un ajuste pendiente, quien ve costos tiene encima de **Aprobar y aplicar** la vista previa **Así queda en los libros fiscales**: entra en el libro de este mes, con el costo que guardó el ajuste al proponerse.',
        ],
      },
      {
        tipo: 'nota',
        tono: 'aviso',
        permiso: APRUEBA_AJUSTES,
        texto: 'En la merma de un traslado, aprobar la da de baja en el origen y rechazar la deja en la existencia del origen. Las dos cosas dan la diferencia por revisada.',
      },
    ],
    mensajes: [
      { mensaje: 'No se puede dar de baja la merma de …: la mercancía que faltó en el traslado ya no está', que_hacer: 'La merma se vendió o se movió antes de aprobarla: rechaza el ajuste o haz un conteo.' },
      { mensaje: '… ya está aprobado (o rechazado)', que_hacer: 'Otra persona lo resolvió antes: cierra la ventana.' },
      { mensaje: '… está desactivado: actívalo en Productos para aprobar …, o recházalo', que_hacer: 'Un producto del ajuste se desactivó después de proponerlo: actívalo en su ficha y aprueba, o rechaza el ajuste.' },
    ],
    manual: '17.2',
    relacionados: ['traslados'],
    claves: ['revisar ajuste', 'aprobar y aplicar', 'rechazar ajuste', 'comentario'],
  },

  // ─── Roturas y detalles ─────────────────────────────────────────────────────
  {
    id: 'roturas',
    titulo: 'Roturas y detalles',
    rutas: ['/roturas'],
    permiso: { clave: 'inventario.reportar' },
    resumen: 'Reporta una lámina de vidrio partida o un producto con detalle (tapa rota, vencido, golpeado), con hasta 5 fotos. La dueña lo aprueba y le pone precio: así pasa a las listas del punto de venta.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Quién hace qué',
        items: [
          'Reporta cualquiera con acceso al inventario: el cajero (que suele verlo primero), almacén y el administrador.',
          'Aprueba, rechaza y decide lo que no se vende la dueña, o quien ella autorice con «Ajustes de inventario» completo.',
          'Poner el precio es de quien fija precios (la dueña). Sin ese permiso, solo se aprueba una rotura sin precio (todo queda como merma) o se rechaza.',
          'Mientras la dueña no lo apruebe, el inventario no cambia y nada pasa a las listas de venta.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          '**Reportar rotura de vidrio** y **Reportar producto con detalle**: los botones de arriba. Para verlos hay que estar operando una tienda.',
          'Pestaña **Reportes**: todo lo reportado, con su estado.',
          'Pestañas **Piezas de vidrio** y **Productos con detalles**: lo que está a la venta como «PIEZAS DE VIDRIOS» y «PRODUCTOS CON DETALLES», y su historial.',
        ],
      },
      { tipo: 'nota', texto: 'Quien reportó recibe un aviso en la campana cuando la dueña aprueba o rechaza su reporte.' },
    ],
    manual: '16.4',
    relacionados: ['pos', 'inventario', 'productos'],
    claves: ['rotura', 'vidrio', 'lámina', 'pieza', 'detalle', 'tapa rota', 'vencido', 'golpeado', 'merma', 'foto', 'PIEZAS DE VIDRIOS', 'PRODUCTOS CON DETALLES'],
  },
  {
    id: 'roturas.reportes',
    padre: 'roturas',
    titulo: 'Pestaña Reportes',
    resumen: 'Todos los reportes de rotura de vidrio y de productos con detalle, con quién los hizo y en qué quedaron.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Reporte**: el número y si es rotura de vidrio o producto con detalle.',
          '**Qué**: la lámina o el producto, cuántas medidas salieron y lo que pasó.',
          '**Cant.**: láminas partidas (**lám.**) o unidades con detalle.',
          '**Almacén**, quién **Reportó** y cuándo.',
          '**Estado**: **Por aprobar**, **Aprobado** o **Rechazado**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Revisar un reporte (dueña)',
        permiso: APRUEBA_AJUSTES,
        pasos: ['Pulsa **Revisar** en un reporte **Por aprobar**.', 'Mira las fotos y lo reportado, arma las piezas o pon el precio especial, y aprueba o rechaza. La ventana tiene su propia ayuda.'],
      },
      { tipo: 'nota', texto: 'Los demás abren cada reporte con **Ver**: las fotos, lo reportado y en qué quedó.' },
    ],
    manual: '16.4',
    claves: ['reportes', 'por aprobar', 'revisar'],
  },
  {
    id: 'roturas.piezas',
    padre: 'roturas',
    titulo: 'Pestaña Piezas de vidrio',
    resumen: 'Las piezas de vidrio aprobadas: lo que está a la venta en «PIEZAS DE VIDRIOS», cuánto queda y a qué precio.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Pieza**: el nombre para la venta, su código y de qué reporte salió.',
          '**Medida** en milímetros.',
          '**Quedan** de las aprobadas («2 de 3») y cuántas se vendieron (**Vendidas**).',
          '**Precio c/u**, **Almacén** y **Estado**.',
          'Debajo del nombre salen las bajas y lo usado en la tienda, con su motivo.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'El filtro Mostrar',
        items: [
          '**En la lista (se venden)**: lo que hoy se ve en el punto de venta. Es lo que sale al abrir.',
          '**Agotadas**: se vendieron o salieron todas. Ya no están en el punto de venta y quedan aquí.',
          '**Por aprobar**, **Merma** (las medidas que la dueña no puso a la venta) y **Todas (historial)**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Lo que no se vende (dueña)',
        permiso: APRUEBA_AJUSTES,
        items: [
          '**Precio**: cambia el precio de lo que queda.',
          '**Dar de baja**: ya no sirve; sale como pérdida.',
          '**Usar en la tienda**: se usa en el negocio; sale de la lista.',
          'Una pieza de vidrio no vuelve a la existencia: la lámina ya se partió.',
          'Estos botones salen solo en lo que está **En la lista**.',
        ],
      },
      { tipo: 'nota', texto: 'En el punto de venta se buscan escribiendo «PIEZAS DE VIDRIOS» (basta «piezas») o con el botón **Piezas de vidrio**.' },
    ],
    manual: '16.4',
    relacionados: ['pos'],
    claves: ['piezas de vidrio', 'pieza', 'agotada', 'merma', 'quedan'],
  },
  {
    id: 'roturas.detalles',
    padre: 'roturas',
    titulo: 'Pestaña Productos con detalles',
    resumen: 'Los productos con detalle aprobados: lo que está a la venta en «PRODUCTOS CON DETALLES», con su precio especial y su precio normal.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Producto**: el nombre, su código y de qué reporte salió.',
          '**Detalle**: lo que tiene («tapa rota»).',
          '**Quedan** de las aprobadas y cuántas se vendieron (**Vendidas**).',
          '**Precio especial**, con el normal debajo para comparar.',
          '**Almacén** y **Estado**.',
        ],
      },
      { tipo: 'lista', titulo: 'El filtro Mostrar', items: ['**En la lista (se venden)** (es lo que sale al abrir), **Agotadas**, **Por aprobar** y **Todas (historial)**.'] },
      {
        tipo: 'lista',
        titulo: 'Lo que no se vende (dueña)',
        permiso: APRUEBA_AJUSTES,
        items: [
          '**Precio**: cambia el precio especial de lo que queda.',
          '**Dar de baja**: ya no sirve; sale como pérdida.',
          '**Usar en la tienda**: por ejemplo, la pintura para pintar la oficina.',
          '**Volver a la existencia**: se puede vender normal. Vuelve a la existencia del producto, a su precio normal.',
        ],
      },
      { tipo: 'nota', texto: 'El producto sigue con su código, su existencia y su precio normal. Solo las unidades aprobadas salen de la existencia normal y se venden con el precio especial.' },
    ],
    manual: '16.4',
    relacionados: ['pos'],
    claves: ['productos con detalles', 'precio especial', 'tapa rota', 'volver a la existencia'],
  },
  {
    id: 'roturas.rotura-vidrio',
    padre: 'roturas',
    titulo: 'Reportar rotura de vidrio',
    resumen: 'Declara la lámina que se partió, con las fotos y las piezas buenas que salieron. La dueña arma las piezas para la venta y les pone precio.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Reportar',
        pasos: [
          'Pulsa **Reportar rotura de vidrio**.',
          'Elige el **Almacén** (viene la tienda).',
          'En **Lámina que se partió**, busca y elige la lámina. Abajo sale su medida, su espesor y su área.',
          'En **Láminas** escribe cuántas se partieron, por ejemplo 1, o 0,5 si era media. Acepta cualquier cantidad mayor que cero, hasta lo disponible.',
          'En **Qué pasó** escribe lo ocurrido («Se partió al descargar el camión»).',
          'En **Fotos de lo que pasó (hasta 5)** toma o elige las fotos, varias a la vez o una por una. Cada una sale en miniatura con su **X** para quitarla. En el teléfono puedes elegir la cámara o la galería.',
          'Declara las piezas buenas: las dos medidas en milímetros (1400 × 3660), cuántas de esa medida y, si quieres, una **Nota (opcional)**. **Otra medida** agrega un renglón.',
          'Pulsa **Enviar reporte**. Queda **Por aprobar** y la dueña recibe el aviso.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Las piezas tienen que caber',
        items: [
          'Abajo dice cuánto ocupan las piezas de la lámina («ocupan 6,12 m² de 8,93 m²») y cuánto queda como merma.',
          'Si una pieza no cabe ni girada, su renglón se marca en rojo. Si entre todas ocupan más que la lámina, el aviso sale en rojo.',
          'Mientras no cuadren, **Enviar reporte** no deja enviar: corrige las medidas.',
          'Si se perdió toda la lámina, deja las medidas en blanco.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Solo salen las láminas con su medida en la ficha. Si no sale, pídele a la dueña que ponga el largo y el ancho en **Productos**.',
          'No se pueden reportar más láminas de las disponibles en ese almacén.',
          'Las fotos no son obligatorias, pero son la prueba que ve la dueña. Van hasta 5 por reporte.',
          'Caben hasta 40 medidas por reporte.',
        ],
      },
      { tipo: 'nota', texto: 'La lámina no sale del inventario al reportar: sale cuando la dueña aprueba el reporte.' },
    ],
    mensajes: [
      { mensaje: 'La pieza de … no cabe en la lámina de …: corrige la medida', que_hacer: 'La pieza es más grande que la lámina, aun girada. Revisa la medida en milímetros.' },
      { mensaje: 'Las piezas suman … m² y la lámina tiene … m²: corrige las medidas', que_hacer: 'Entre todas ocupan más que la lámina partida. Revisa las medidas y las cantidades.' },
      { mensaje: '… no tiene la medida de la lámina en su ficha', que_hacer: 'Que la dueña ponga el largo y el ancho en la ficha del producto.' },
      { mensaje: 'Ninguna lámina con esa búsqueda tiene su medida en la ficha', que_hacer: 'Busca con otra palabra, o pídele a la dueña que ponga la medida en **Productos**.' },
      { mensaje: 'En ese almacén hay … disponibles y se reportan … láminas', que_hacer: 'No se reporta más de lo disponible. Revisa el almacén o las láminas.' },
      { mensaje: 'Elige la lámina que se partió', que_hacer: 'Busca y elige la lámina antes de enviar.' },
      { mensaje: 'Indica qué pasó con la lámina', que_hacer: 'Escribe en **Qué pasó** lo que ocurrió.' },
      { mensaje: 'Revisa las piezas: medida en milímetros y cuántas', que_hacer: 'Cada renglón lleva las dos medidas en milímetros y cuántas son, en número entero.' },
      { mensaje: 'La foto está incompleta o no es una imagen: vuelve a tomarla', que_hacer: 'Toma la foto de nuevo, o elige otra imagen.' },
      REINTENTO,
    ],
    manual: '16.4',
    claves: ['rotura de vidrio', 'lámina partida', 'piezas buenas', 'medida', 'milímetros', 'foto', 'merma'],
  },
  {
    id: 'roturas.producto-detalle',
    padre: 'roturas',
    titulo: 'Reportar producto con detalle',
    resumen: 'Reporta unidades con un detalle (tapa rota, vencido, golpeado) para venderlas con un precio especial. La dueña pone el precio y aprueba.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Reportar',
        pasos: [
          'Pulsa **Reportar producto con detalle**.',
          'Elige el **Almacén** (viene la tienda) y escribe **Cuántos** son, en unidades enteras.',
          'En **Producto**, busca y elige el producto.',
          'Escribe el **Detalle** («Tapa rota»).',
          'Toma o elige las fotos, hasta 5 (varias a la vez o una por una).',
          'Pulsa **Enviar reporte**. Queda **Por aprobar** y la dueña recibe el aviso.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Mientras no se apruebe, esas unidades se siguen vendiendo normal, a su precio normal.',
          'Al aprobarse, salen de la existencia normal y pasan a «PRODUCTOS CON DETALLES» con su precio especial.',
          'No se pueden reportar más unidades de las disponibles en ese almacén. Los servicios no salen.',
          'Las fotos no son obligatorias, pero son la prueba que ve la dueña. Van hasta 5 por reporte.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Elige el producto', que_hacer: 'Busca y elige el producto antes de enviar.' },
      { mensaje: 'Indica cuántos son (unidades enteras)', que_hacer: 'Escribe en **Cuántos** un número entero, de 1 en adelante.' },
      { mensaje: 'Indica el detalle (tapa rota, vencido…)', que_hacer: 'Escribe en **Detalle** lo que tiene el producto.' },
      { mensaje: 'En ese almacén hay … disponibles y se reportan … con detalle', que_hacer: 'Baja la cantidad o elige otro almacén.' },
      REINTENTO,
    ],
    manual: '16.4',
    claves: ['producto con detalle', 'tapa rota', 'vencido', 'golpeado', 'precio especial', 'foto'],
  },
  {
    id: 'roturas.revisar',
    padre: 'roturas',
    titulo: 'Revisar un reporte',
    resumen: 'La dueña ve las fotos y lo reportado, arma las piezas o pone el precio especial, y aprueba o rechaza. Los demás ven aquí en qué quedó su reporte.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Aprobar una rotura de vidrio',
        permiso: APRUEBA_AJUSTES,
        pasos: [
          'Pulsa **Revisar** en el reporte. La primera foto sale grande y las demás en miniatura: pulsa una para verla grande, pasa con **Anterior** y **Siguiente**, y pulsa otra vez para volver.',
          'En cada medida, revisa el **Nombre para la venta**: viene listo («Vidrio claro 3mm 1400x3660») y se puede cambiar.',
          'En **Cantidad** deja las reportadas o menos.',
          'Escribe el **Precio c/u $** de cada medida que se va a vender.',
          'Pulsa **Aprobar**. La lámina sale del inventario y las piezas con precio pasan a «PIEZAS DE VIDRIOS».',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Aprobar un producto con detalle',
        permiso: APRUEBA_AJUSTES,
        pasos: [
          'Pulsa **Revisar**. Junto a lo reportado sale el precio normal del producto.',
          'Escribe el **Precio especial $**.',
          'Pulsa **Aprobar**. Esas unidades salen de la existencia normal y pasan a «PRODUCTOS CON DETALLES».',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Rechazar',
        permiso: APRUEBA_AJUSTES,
        pasos: ['Escribe el motivo en **Comentario (obligatorio para rechazar)**.', 'Pulsa **Rechazar**. No se mueve nada.'],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'La medida sin precio, o con cantidad 0, queda como merma. Lo que apruebes de menos, también.',
          'Un producto con detalle se aprueba con todas las unidades reportadas. Si son otras, recházalo y que lo reporten de nuevo.',
          'Si un producto con detalle no se va a vender, recházalo.',
          'Poner precio es fijar un precio: lo hace quien tiene el permiso de precios.',
          'Al aprobar, el comentario es opcional y queda guardado.',
        ],
      },
      { tipo: 'nota', texto: 'Quien no aprueba abre el reporte con **Ver**. Mientras está pendiente dice que lo aprueba la dueña; ya resuelto, quién lo resolvió, cuándo y su comentario.' },
    ],
    mensajes: [
      { mensaje: 'Aprobar un reporte con precios requiere el permiso de fijar precios', que_hacer: 'Ponerle precio a una pieza o a un producto con detalle es fijar un precio: que lo apruebe quien fija precios (la dueña).' },
      { mensaje: 'Pon el precio especial (o rechaza el reporte si no se va a vender)', que_hacer: 'Escribe el precio, o rechaza el reporte.' },
      { mensaje: 'Revisa las cantidades: enteras y a lo sumo las reportadas', que_hacer: 'Corrige la **Cantidad** marcada en rojo.' },
      { mensaje: 'Escribe el motivo del rechazo', que_hacer: 'Escribe el motivo en el comentario y pulsa **Rechazar** otra vez.' },
      { mensaje: '… ya fue aprobado (o rechazado)', que_hacer: 'Otra persona lo resolvió antes: cierra la ventana.' },
    ],
    manual: '16.4',
    claves: ['revisar reporte', 'aprobar', 'rechazar', 'nombre para la venta', 'precio c/u', 'precio especial'],
  },
  {
    id: 'roturas.precio',
    padre: 'roturas',
    titulo: 'Cambiar el precio',
    permiso: FIJA_PRECIOS,
    resumen: 'Cambia el precio de lo que queda de una pieza de vidrio o de un producto con detalle.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Cambiar el precio',
        pasos: [
          'En **Piezas de vidrio** o **Productos con detalles**, pulsa **Precio** en la fila.',
          'Arriba dice cuántas quedan y, en un producto con detalle, su precio normal.',
          'Escribe el **Precio de venta $ (cada uno)**.',
          'Pulsa **Guardar precio**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Solo se cambia lo que está **En la lista**. Lo ya vendido conserva su precio.',
          'El precio tiene que ser mayor que cero. Para sacarlo de la venta, usa **Dar de baja** o **Usar en la tienda**.',
          'El cambio queda en **Auditoría** con el precio anterior.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'El precio debe ser mayor que cero', que_hacer: 'Escribe un precio mayor que cero.' },
      { mensaje: '… ya no está en la lista: no se le cambia el precio', que_hacer: 'Se agotó o salió de la lista mientras tanto.' },
    ],
    manual: '16.4',
    claves: ['precio pieza', 'cambiar precio', 'precio especial'],
  },
  {
    id: 'roturas.salida',
    padre: 'roturas',
    titulo: 'Dar de baja, usar en la tienda o volver a la existencia',
    permiso: APRUEBA_AJUSTES,
    resumen: 'Lo que no se vende lo decides tú, en cada caso y siempre con el motivo: darlo de baja, usarlo en la tienda o, en un producto con detalle, volverlo a la existencia normal.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar la salida',
        pasos: [
          'Pulsa **Dar de baja**, **Usar en la tienda** o **Volver a la existencia** en la fila.',
          'En **Qué pasa** queda lo que elegiste; puedes cambiarlo.',
          'En **Cuántos** viene todo lo que queda. Bájalo si es solo una parte.',
          'Escribe el **Motivo** (al menos 3 letras).',
          'Pulsa el botón de abajo: **Dar de baja (pérdida)**, **Uso en la tienda** o **Volver a la existencia normal**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa con cada una',
        items: [
          '**Dar de baja (pérdida)**: ya no sirve; sale de la lista como pérdida.',
          '**Uso en la tienda**: se usa en el negocio (por ejemplo, pintar la oficina); sale de la lista.',
          '**Volver a la existencia normal** (solo productos con detalles): vuelve a la existencia del producto y se vende a su precio normal. Si el almacén del lote está desactivado, no vuelve: actívalo en **Configuración**, o da de baja o usa en la tienda lo que queda.',
          'Si no queda nada, sale de la lista y queda en el historial como **Agotada**.',
          'Las bajas y lo usado en la tienda salen en el reporte de inventario, con las mermas.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Una salida no se deshace. Revisa la cantidad antes de confirmar.' },
    ],
    mensajes: [
      { mensaje: 'De … quedan …: otra caja se llevó las demás', que_hacer: 'Mientras decidías se vendieron unidades. Baja la cantidad.' },
      { mensaje: 'Escribe el motivo', que_hacer: 'Escribe en **Motivo** por qué sale.' },
      { mensaje: '… está desactivado: lo que queda del lote no puede volver a su existencia. Actívalo en Configuración, o da de baja o usa en la tienda lo que queda.', que_hacer: 'El almacén del lote está desactivado: actívalo, o elige **Dar de baja** o **Usar en la tienda**.' },
    ],
    manual: '16.4',
    relacionados: ['reportes'],
    claves: ['dar de baja', 'pérdida', 'uso en la tienda', 'uso interno', 'volver a la existencia', 'reintegro'],
  },

  // ─── Traslados ──────────────────────────────────────────────────────────────
  {
    id: 'traslados',
    titulo: 'Traslados',
    rutas: ['/traslados'],
    permiso: { clave: 'traslados.despachar' },
    resumen: 'Mueve mercancía entre almacenes, de tu sucursal o de otra, con su guía. Sale del origen al despachar y entra al destino solo cuando el destino confirma la recepción.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La pantalla',
        items: [
          'Pestaña **Por recibir**: las guías que vienen hacia tu sucursal.',
          'Pestaña **Todos**: todas las guías, con filtros.',
          '**Nuevo traslado**: despacha una guía.',
          'Un clic en una guía abre su detalle: recibir, imprimir la guía o cancelar.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'Las tiendas no reciben compras: reciben traslados.',
          'Solo viaja lo disponible: lo apartado para clientes se queda.',
          'Solo recibe un usuario de la sucursal de destino.',
          'La guía es un documento interno, no fiscal.',
        ],
      },
    ],
    manual: '18',
    relacionados: ['flota', 'ajustes', 'inventario', 'impresion'],
    claves: ['traslado', 'guía', 'despachar', 'recibir', 'tránsito', 'depósito', 'transferencia'],
  },
  {
    id: 'traslados.por-recibir',
    padre: 'traslados',
    titulo: 'Pestaña Por recibir',
    resumen: 'Las guías en tránsito, o recibidas en parte, que vienen hacia tu sucursal.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          '**Guía**, **Dirección** (**Entrante**, **Saliente** o **Interno**), de dónde sale y a dónde llega.',
          '**Despachado**: cuándo, quién, y el vehículo y el chofer si se cargaron.',
          '**Líneas** y **Enviado / recibido**.',
          '**Estado**: **En tránsito** o **Recibido parcial**.',
          'El buscador busca por guía o producto.',
        ],
      },
      { tipo: 'pasos', titulo: 'Recibir', pasos: ['Pulsa la guía para abrirla.', 'Pulsa **Recibir** y sigue la ayuda de la guía.'] },
      { tipo: 'nota', texto: 'El destino recibe un aviso en la campana cuando le despachan una guía.' },
    ],
    manual: '18.2',
    claves: ['por recibir', 'en tránsito', 'entrante'],
  },
  {
    id: 'traslados.todos',
    padre: 'traslados',
    titulo: 'Pestaña Todos',
    resumen: 'Todas las guías de tu sucursal, las que salen y las que llegan, con filtros para encontrar una.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'Filtros',
        items: [
          'Estado: **Todos los estados**, **En tránsito**, **Recibido parcial**, **Recibido** o **Cancelado**.',
          '**Desde** y **Hasta**: la fecha de despacho.',
          '**Sale de** y **Llega a**: el almacén de origen y el de destino.',
          '**Diferencias**: **Con diferencia** o **Sin diferencia**.',
          'El buscador busca por guía o producto. Al lado sale cuántas guías hay.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Las diferencias',
        items: [
          'Una guía con diferencia dice su causa (quedó en el origen, merma en el traslado, o parte y parte) y si está **revisada** o **por revisar**.',
          'Mientras no se revisa, la guía cuenta como alerta de la tienda de origen en el consolidado.',
        ],
      },
      { tipo: 'nota', texto: 'La dueña, sin sucursal elegida, ve las guías de todas las tiendas.' },
    ],
    manual: '18.5',
    claves: ['buscar guía', 'historial de traslados', 'diferencia', 'cancelado'],
  },
  {
    id: 'traslados.nuevo',
    padre: 'traslados',
    titulo: 'Nuevo traslado',
    resumen: 'Despacha mercancía de un almacén de tu sucursal a otro almacén. Al despachar sale del origen y queda en tránsito hasta que el destino la recibe.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Despachar',
        pasos: [
          'Pulsa **Nuevo traslado**.',
          '**Almacén origen (mi sucursal)**: viene la tienda. En **Almacén destino** elige a dónde va, de tu sucursal o de otra.',
          'Busca cada producto en **Buscar producto en el almacén origen…** y pulsa el que es. La lista dice lo disponible de cada uno.',
          'En **Enviar** escribe cuánto va (cada producto entra con 1). Si pasa de lo disponible, la fila se pone roja.',
          'Si quieres, escribe el **Motivo (opcional)**.',
          'Transporte, todo opcional: **Vehículo (opcional)** de la flota, u **Otro (escribirlo)** con **Vehículo y placa**; **Chofer (opcional)** y **Cédula del chofer**.',
          'Pulsa **Despachar**.',
          'Se abre una ventana que se queda en pantalla: pulsa **Imprimir guía** y después **Listo**. Al cerrarla se abre el detalle del traslado.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Para tener en cuenta',
        items: [
          'Solo viaja lo **disponible**: lo apartado para clientes se queda.',
          'Cambiar el almacén de origen vacía la lista de productos.',
          '**Despachar** se activa cuando hay origen, un destino distinto y todas las cantidades caben en lo disponible.',
          'Lo que no cargues del transporte sale en la guía como una línea en blanco para llenarla a mano.',
          'La guía recibe su número, la mercancía queda **En tránsito** y el destino recibe el aviso por la campana.',
          'Mientras busca, la lista dice «Buscando en el almacén de origen…».',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Si sale «No se pudo despachar» por un corte de conexión, pulsa **Despachar** otra vez sin cambiar nada: no sale una segunda guía ni se descuenta la mercancía dos veces.' },
    ],
    mensajes: [
      { mensaje: 'No se pudo despachar', que_hacer: 'Lee el motivo debajo. Si fue la conexión, pulsa **Despachar** otra vez sin cambiar nada.' },
      { mensaje: '…: disponible … en … (existencia …, reservado …) y se quieren trasladar …', que_hacer: 'Baja la cantidad a lo disponible: lo reservado para clientes no viaja.' },
      { mensaje: 'El almacén destino debe ser distinto del origen', que_hacer: 'Elige otro **Almacén destino**.' },
      { mensaje: 'Ese vehículo está desactivado en la ficha de flota', que_hacer: 'Elige otro vehículo, u **Otro (escribirlo)**.' },
      REINTENTO,
    ],
    manual: '18.1',
    relacionados: ['flota', 'impresion'],
    claves: ['nuevo traslado', 'despachar', 'guía', 'vehículo', 'chofer', 'placa'],
  },
  {
    id: 'traslados.guia',
    padre: 'traslados',
    titulo: 'La guía: recibir, diferencias y cancelar',
    resumen: 'El detalle de un traslado: qué se envió y qué llegó, el transporte y las recepciones. Desde aquí se recibe, se cierra con diferencia, se cancela y se imprime la guía.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Recibir',
        pasos: [
          'Abre la guía en **Por recibir** y pulsa **Recibir**.',
          '**Recibir ahora** viene con todo lo pendiente. Cambia lo que llegó distinto. Quien recibe ve cantidades, no costos.',
          'Si quieres, escribe **Observaciones**.',
          'Pulsa **Confirmar recepción**. Lo recibido entra al almacén de destino.',
          'Si llegó solo una parte y el resto viene después, no marques nada más: la guía queda **Recibido parcial** y sigue en **Por recibir** por lo que falta.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Recibir con diferencia (no va a llegar nada más)',
        pasos: [
          'Marca **Cerrar la guía: no va a llegar nada más**.',
          'En **Lo que falta**, elige la causa de cada producto que no llegó completo: **Se quedó en el origen** o **Merma (se rompió o se perdió)**.',
          'Escribe **Qué observaste** (al menos 5 letras): lo revisa la dueña.',
          'Pulsa **Confirmar recepción**.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Qué pasa con una diferencia',
        items: [
          'Todo lo que faltó vuelve a la existencia del origen.',
          'Lo que **se quedó en el origen** termina ahí. La guía queda por revisar hasta que la dueña (o quien apruebe ajustes) pulsa **Dar el visto bueno**, con un **Comentario (opcional)**. Quien recibió la guía no puede darlo.',
          'Lo que fue **merma** genera un ajuste **Merma** pendiente en la tienda de origen. Al aprobarlo o rechazarlo en **Ajustes**, la diferencia queda revisada.',
          'Se avisa a los administradores de las dos tiendas y a la dueña.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cancelar un traslado',
        pasos: ['Pulsa **Cancelar traslado**.', 'Escribe el motivo y pulsa **Confirmar cancelación**. La existencia vuelve al origen.'],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Solo cancela un administrador del origen, solo en tránsito y solo si no se ha recibido nada. Una cancelación no se deshace.' },
      {
        tipo: 'lista',
        titulo: 'Qué más muestra',
        items: [
          'El transporte (vehículo, placa, chofer y cédula) y cada recepción, con quién la hizo y si cerró la guía.',
          '**Imprimir guía**: media carta vertical, con el RIF, las direcciones de origen y destino, el transporte, los productos y las firmas de quien despacha, transporta y recibe.',
        ],
      },
    ],
    mensajes: [
      { mensaje: 'Solo un usuario de … (destino) puede recibir el traslado', que_hacer: 'La guía es de otra sucursal: que la reciba el destino.' },
      { mensaje: 'Llegó menos de lo enviado: indica, de cada producto que falta, si se quedó en el origen o fue merma', que_hacer: 'Elige la causa de cada producto y escribe lo que observaste.' },
      { mensaje: 'No indicaste ninguna cantidad recibida', que_hacer: 'Escribe lo que llegó o, si no va a llegar nada más, cierra la guía y di qué pasó con lo que falta.' },
      { mensaje: '…: pendiente por recibir … y se declaran …', que_hacer: 'No se recibe más de lo pendiente: baja la cantidad.' },
      { mensaje: 'Quien recibió la guía no revisa su propia diferencia', que_hacer: 'El visto bueno lo da la dueña.' },
      { mensaje: 'Ya tiene recepciones registradas: ciérralo desde el destino', que_hacer: 'Un traslado con algo recibido, o recibido en parte, no se cancela: el destino lo cierra con **Cerrar la guía: no va a llegar nada más**.' },
      { mensaje: 'El traslado … ya está recibido: solo se cancela mientras va en camino', que_hacer: 'La guía ya se recibió completa (o ya se canceló, y el mensaje dice «cancelado»): no hay nada que cancelar.' },
      { mensaje: 'No se pudo recibir', que_hacer: 'Lee el motivo debajo. Si fue la conexión, pulsa **Confirmar recepción** otra vez sin cambiar nada: no se recibe dos veces.' },
    ],
    manual: '18.2',
    relacionados: ['ajustes', 'impresion'],
    claves: ['recibir', 'confirmar recepción', 'diferencia', 'merma', 'visto bueno', 'cancelar traslado', 'imprimir guía'],
  },

  // ─── Flota ──────────────────────────────────────────────────────────────────
  {
    id: 'flota',
    titulo: 'Flota y equipos',
    rutas: ['/flota'],
    permiso: { clave: 'flota.gestionar' },
    resumen: 'La ficha de cada vehículo y equipo de la empresa (carro, camión, montacargas, planta…), su historial de mantenimiento y los viajes en que se usó.',
    bloques: [
      {
        tipo: 'lista',
        titulo: 'La lista',
        items: [
          'Filtra por tipo (**Todos los tipos**), busca por nombre, placa, marca o serial, y marca **Ver también los desactivados** para verlos todos.',
          'Cada equipo con su placa o serial, su **Tienda** (o **De la empresa**), el **Último mantenimiento**, el **Kilometraje**, cuántos **Mantenimientos**, lo **Gastado** y los **Viajes**.',
          'Un clic en la fila abre su ficha.',
        ],
      },
      {
        tipo: 'lista',
        titulo: 'Reglas',
        items: [
          'El equipo queda en la tienda desde la que se registra. Si la dueña lo registra sin sucursal elegida, queda **De la empresa**: lo ven y lo usan todas las tiendas.',
          'La ficha de un equipo de la empresa la cambia solo la dueña. Las tiendas pueden registrarle mantenimiento.',
          'La placa no se puede repetir.',
          'Un equipo no se borra: se desactiva y conserva su historial.',
          'El mantenimiento es un registro de gestión: no es un gasto ni una compra. No toca caja, libros ni inventario.',
          'El sistema no tiene alertas ni plazos de mantenimiento.',
        ],
      },
    ],
    manual: '19',
    relacionados: ['traslados'],
    claves: ['flota', 'vehículo', 'camión', 'carro', 'montacargas', 'planta eléctrica', 'equipo', 'mantenimiento'],
  },
  {
    id: 'flota.equipo',
    padre: 'flota',
    titulo: 'Nuevo equipo o editar su ficha',
    resumen: 'Los datos de un vehículo o equipo. Los vehículos activos (carro, camión, moto) se pueden elegir al despachar un traslado.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un equipo',
        pasos: [
          'Pulsa **Nuevo equipo**.',
          'Elige el **Tipo**: Carro, Camión, Moto, Montacargas, Planta eléctrica u Otro equipo.',
          'Escribe el **Nombre**, como lo llaman en la tienda («Camión 350 blanco»).',
          'Si aplica: **Placa**, **Serial**, **Marca**, **Modelo**, **Año** y **Notas**.',
          'Pulsa **Guardar**. Se abre su ficha.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Cambiar la ficha o desactivar el equipo',
        pasos: [
          'En la ficha del equipo, pulsa **Editar ficha**.',
          'Cambia los datos. Para sacarlo de uso, desmarca **Activo**: deja de ofrecerse en los traslados y su historial se conserva.',
          'Pulsa **Guardar**.',
        ],
      },
      { tipo: 'lista', titulo: 'Para tener en cuenta', items: ['**Guardar** no se activa sin un nombre de al menos 2 letras, ni con un año inválido.', 'La placa se escribe en mayúsculas y no se puede repetir.'] },
    ],
    mensajes: [
      { mensaje: 'La placa … ya es de «…»', que_hacer: 'Esa placa ya está registrada: busca ese equipo en la lista.' },
      { mensaje: 'Los equipos de la empresa los edita la dueña. Tú puedes registrarles mantenimiento.', que_hacer: 'Pídele el cambio a la dueña.' },
    ],
    manual: '19.1',
    claves: ['nuevo equipo', 'placa', 'serial', 'desactivar equipo', 'activo'],
  },
  {
    id: 'flota.ficha',
    padre: 'flota',
    titulo: 'Ficha del equipo: mantenimientos y viajes',
    resumen: 'El resumen del equipo, su historial de mantenimiento y los viajes (traslados) en que se usó.',
    bloques: [
      {
        tipo: 'pasos',
        titulo: 'Registrar un mantenimiento',
        pasos: [
          'Abre la ficha del equipo y pulsa **Registrar mantenimiento**.',
          '**Fecha** (no puede ser futura), **Qué se le hizo** (Cambio de aceite, Cauchos, Reparación, Repuesto / pieza, Revisión u Otro), **Costo ($)** y **Detalle** (al menos 3 letras).',
          'Si aplica: **Kilometraje**, **Horas de uso** (planta, montacargas) y **Taller o quién lo hizo**.',
          'Pulsa **Guardar mantenimiento**.',
        ],
      },
      {
        tipo: 'pasos',
        titulo: 'Anular un mantenimiento mal cargado',
        pasos: ['Pulsa **Anular** en su fila.', 'Escribe el **Motivo de la anulación** y pulsa **Anular**.', 'Queda tachado en el historial y deja de sumar.'],
      },
      {
        tipo: 'lista',
        titulo: 'Qué muestra',
        items: [
          'Arriba: cuántos **Mantenimientos**, lo **Gastado**, el último **Kilometraje** y los **Viajes**.',
          '**Historial de mantenimiento**: fecha, trabajo, km u horas, costo y quién lo registró.',
          '**Viajes (traslados)**: guía, fecha, de dónde salió y a dónde llegó, chofer, unidades y estado. La guía abre el traslado.',
          'El vehículo se elige al despachar la guía. Solo se ofrecen carros, camiones y motos activos. Quien despacha ve el nombre y la placa, no los costos.',
        ],
      },
      { tipo: 'nota', tono: 'aviso', texto: 'Un mantenimiento no se edita ni se borra: se anula con su motivo.' },
    ],
    mensajes: [
      { mensaje: 'La fecha del mantenimiento no puede ser futura', que_hacer: 'Pon la fecha de hoy o una anterior.' },
      { mensaje: 'Ese mantenimiento ya está anulado', que_hacer: 'No hay que hacer nada más.' },
    ],
    manual: '19.2',
    relacionados: ['traslados'],
    claves: ['mantenimiento', 'aceite', 'cauchos', 'reparación', 'kilometraje', 'anular mantenimiento', 'viajes'],
  },
];
